import { motion, useReducedMotion } from 'motion/react';
import {
  Activity,
  useState,
  useRef,
  useCallback,
  useEffect,
  type ReactNode,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { EASE, PANEL_MORPH_S } from '@/lib/animate';
import { classList } from '@/utils/cn';

type CollapseOptions = { instant?: boolean };
type ExpandOptions = { instant?: boolean; initial?: boolean };

export interface ResizableBoxHandle {
  /** Collapse the resizable box to 0px width while preserving the current width */
  collapse: (options?: CollapseOptions) => void;
  /** Expand the resizable box back to its previous width before collapse */
  expand: (options?: ExpandOptions) => void;
  /** Check if the resizable box is currently collapsed */
  isCollapsed: () => boolean;
  /** Get the current width of the resizable box in pixels */
  getWidth: () => number;
}

interface ResizableBoxProps {
  /** The content to be rendered inside the resizable container */
  children: ReactNode;
  /** The initial width of the resizable box in pixels. Default: 300 */
  initialWidth?: number;
  /** The minimum width the box can be resized to in pixels. Default: 200 */
  minWidth?: number;
  /** The maximum width the box can be resized to in pixels. Default: 600 */
  maxWidth?: number;
  /** Additional CSS classes to apply to the container */
  className?: string;
  /**
   * When true, automatically collapses the box to 0px width when user resizes below minimum width.
   * Provides a snap-to-collapse behavior for easy collapsing without dragging all the way.
   */
  snapCollapse?: boolean;
  /**
   * Unique identifier for persistence. When provided, the component will:
   * - Save the current width to localStorage when resized
   * - Restore the saved width on mount (if within min/max bounds)
   * - Storage key format: `resizable-box-${id}`
   */
  id?: string;
  /** Callback fired when the resizable box is collapsed */
  onCollapse?: () => void;
  /** Fired when a drag commits a snap-collapse (in addition to onCollapse). */
  onDragCollapse?: () => void;
  /** Callback fired when the resizable box is expanded */
  onExpand?: () => void;
  /** Callback fired when resize starts (drag begins) */
  onResizeStart?: () => void;
  /** Callback fired when resize ends (drag ends) */
  onResizeEnd?: () => void;
  /** Callback fired during resize with current width */
  onResize?: (width: number) => void;
  /** Positioning classes for the drag handle, e.g. to extend it beyond the box. */
  handleClassName?: string;
  handleIndicatorClassName?: string;
}

interface StoredState {
  width: number;
  isCollapsed: boolean;
  lastNonCollapsedWidth?: number;
}

const getStorageKey = (id: string) => `resizable-box-${id}`;

const getStoredState = (id: string): StoredState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(getStorageKey(id));
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (
      typeof parsed === 'object' &&
      typeof parsed.width === 'number' &&
      typeof parsed.isCollapsed === 'boolean'
    ) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
};

const setStoredState = (id: string, state: StoredState) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(getStorageKey(id), JSON.stringify(state));
  } catch {
    // Do nothing
  }
};

interface DragHandleProps {
  /** Whether the drag handle is currently being dragged */
  isDragging?: boolean;
  /** Mouse down event handler for starting drag operations */
  onMouseDown: (e: React.MouseEvent) => void;
  /** Additional CSS classes to apply to the drag handle */
  className?: string;
  indicatorClassName?: string;
}

export const ResizableBox = forwardRef<ResizableBoxHandle, ResizableBoxProps>(
  (
    {
      children,
      initialWidth = 300,
      minWidth = 200,
      maxWidth = 600,
      className,
      id,
      snapCollapse = false,
      onCollapse,
      onDragCollapse,
      onExpand,
      onResizeStart,
      onResizeEnd,
      onResize,
      handleClassName = 'absolute right-0 top-0 bottom-0',
      handleIndicatorClassName = 'inset-y-0',
    },
    ref
  ) => {
    const getInitialState = () => {
      if (id) {
        const storedState = getStoredState(id);
        if (storedState) {
          const validWidth =
            storedState.lastNonCollapsedWidth ||
            (storedState.width > 0 ? storedState.width : initialWidth);
          const width =
            validWidth >= minWidth && validWidth <= maxWidth
              ? validWidth
              : initialWidth;
          return {
            width: storedState.isCollapsed ? 0 : width,
            isCollapsed: storedState.isCollapsed,
            previousWidth: width,
          };
        }
      }
      return {
        width: initialWidth,
        isCollapsed: false,
        previousWidth: initialWidth,
      };
    };

    const initialState = getInitialState();
    const [width, setWidth] = useState(initialState.width);
    const [isCollapsed, setIsCollapsed] = useState(initialState.isCollapsed);
    const [isDragging, setIsDragging] = useState(false);
    const [instantTransition, setInstantTransition] = useState(false);
    // true while an animated collapse plays; it commits when the width
    // animation settles, and an expand before then reverses it
    const [isClosing, setIsClosing] = useState(false);
    const isHidden = isCollapsed || isClosing;
    const reduceMotion = useReducedMotion();
    const boxRef = useRef<HTMLDivElement>(null);
    const isResizing = useRef(false);
    // true while a drag has visually snapped to zero but not yet committed
    const dragSnappedRef = useRef(false);
    const initialX = useRef(0);
    const initialWidthRef = useRef(0);
    const previousWidth = useRef(
      initialState.previousWidth || initialState.width
    );

    const finishCollapse = useCallback(() => {
      setIsClosing(false);
      setWidth(0);
      setIsCollapsed(true);
      onCollapse?.();
      onResize?.(0);
    }, [onCollapse, onResize]);

    const collapse = useCallback(
      (options?: CollapseOptions & { preserveWidth?: boolean }) => {
        if (isHidden) return;
        if (!options?.preserveWidth) {
          previousWidth.current = width;
        }
        if (options?.instant) {
          setInstantTransition(true);
          finishCollapse();
        } else {
          setIsClosing(true);
        }
      },
      [width, isHidden, finishCollapse]
    );

    const collapseRef = useRef(collapse);

    collapseRef.current = collapse;

    useEffect(() => {
      if (!id) return;
      if (!isDragging) {
        const currentStored = getStoredState(id);
        const lastNonCollapsed =
          !isCollapsed && width > 0
            ? width
            : currentStored?.lastNonCollapsedWidth || previousWidth.current;

        setStoredState(id, {
          width,
          // a collapse in flight is stored as collapsed, so an unmount
          // before it commits doesn't come back expanded
          isCollapsed: isHidden,
          lastNonCollapsedWidth:
            lastNonCollapsed > 0 ? lastNonCollapsed : initialWidth,
        });
      }
    }, [id, width, isCollapsed, isHidden, isDragging, initialWidth]);

    useImperativeHandle(
      ref,
      () => ({
        collapse,
        expand: (options?: ExpandOptions) => {
          if (isClosing) {
            setIsClosing(false);
          } else if (isCollapsed) {
            const expandWidth = options?.initial
              ? initialWidth
              : previousWidth.current;
            if (options?.instant) setInstantTransition(true);
            setWidth(expandWidth);
            setIsCollapsed(false);
            onExpand?.();
            onResize?.(expandWidth);
          }
        },
        isCollapsed: () => isCollapsed,
        getWidth: () => width,
      }),
      [
        collapse,
        width,
        initialWidth,
        isCollapsed,
        isClosing,
        onExpand,
        onResize,
      ]
    );

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!isResizing.current || isCollapsed) return;

        const deltaX = e.clientX - initialX.current;
        const newWidth = initialWidthRef.current + deltaX;

        // Update global cursor based on drag direction
        if (deltaX < 0) {
          // Dragging left
          document.body.style.cursor = 'w-resize';
        } else if (deltaX > 0) {
          // Dragging right
          document.body.style.cursor = 'e-resize';
        } else {
          // No movement
          document.body.style.cursor = 'ew-resize';
        }

        if (newWidth >= minWidth && newWidth <= maxWidth) {
          dragSnappedRef.current = false;
          setWidth(newWidth);
          previousWidth.current = newWidth;
          onResize?.(newWidth);
        } else if (newWidth > 0 && newWidth < minWidth) {
          if (snapCollapse && newWidth <= minWidth / 2.5) {
            // visual snap only: the collapse commits on mouseup, so dragging
            // back out within the same hold re-expands
            dragSnappedRef.current = true;
            previousWidth.current = initialWidthRef.current;
            setWidth(0);
            onResize?.(0);
          } else {
            dragSnappedRef.current = false;
            setWidth(minWidth);
            previousWidth.current = minWidth;
            onResize?.(minWidth);
          }
        } else if (newWidth > maxWidth) {
          dragSnappedRef.current = false;
          setWidth(maxWidth);
          previousWidth.current = maxWidth;
          onResize?.(maxWidth);
        }
      },
      [maxWidth, minWidth, onResize, isCollapsed, snapCollapse]
    );

    const handleMouseUp = useCallback(() => {
      isResizing.current = false;
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'auto';

      Array.from(document.body.children).forEach((child) => {
        (child as HTMLElement).style.pointerEvents = 'auto';
      });

      if (dragSnappedRef.current) {
        dragSnappedRef.current = false;
        collapseRef.current({ instant: true, preserveWidth: true });
        onDragCollapse?.();
      }

      onResizeEnd?.();
    }, [handleMouseMove, onResizeEnd, onDragCollapse]);

    useEffect(() => {
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'auto';

        Array.from(document.body.children).forEach((child) => {
          (child as HTMLElement).style.pointerEvents = 'auto';
        });
      };
    }, [handleMouseMove, handleMouseUp]);

    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        isResizing.current = true;
        setIsDragging(true);
        initialX.current = e.clientX;
        initialWidthRef.current = width;

        document.body.style.cursor = 'ew-resize';
        Array.from(document.body.children).forEach((child) => {
          (child as HTMLElement).style.pointerEvents = 'none';
        });

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        onResizeStart?.();
        e.preventDefault();
      },
      [width, handleMouseMove, handleMouseUp, onResizeStart]
    );

    return (
      <motion.div
        className={classList(
          'relative flex',
          isDragging && 'will-change-[width,opacity]',
          className
        )}
        ref={boxRef}
        animate={{
          width: isHidden ? 0 : width,
          opacity: isHidden ? 0 : 1,
        }}
        transition={{
          type: 'tween',
          duration:
            isDragging || instantTransition || reduceMotion ? 0 : PANEL_MORPH_S,
          ease: EASE.outQuint,
        }}
        onAnimationComplete={() => {
          if (instantTransition) setInstantTransition(false);
          if (isClosing) finishCollapse();
        }}
        initial={false}
      >
        <div
          inert={isHidden}
          className={classList(
            'relative w-full overflow-hidden',
            isDragging && 'pointer-events-none'
          )}
        >
          <div className="absolute inset-y-0 right-0" style={{ width }}>
            <Activity mode={isCollapsed ? 'hidden' : 'visible'}>
              {children}
            </Activity>
          </div>
        </div>

        {/* Resizer handle - hidden while collapsed or collapsing */}
        {!isHidden && (
          <DragHandle
            isDragging={isDragging}
            onMouseDown={handleMouseDown}
            className={handleClassName}
            indicatorClassName={handleIndicatorClassName}
          />
        )}
      </motion.div>
    );
  }
);

ResizableBox.displayName = 'ResizableBox';

const DragHandle = ({
  isDragging,
  onMouseDown,
  className,
  indicatorClassName,
}: DragHandleProps) => {
  return (
    <div
      data-dragging={isDragging ? 'true' : 'false'}
      className={classList(
        'group',
        'w-[6px]',
        'transition-colors',
        'group relative',
        'flex justify-end',
        'cursor-ew-resize',
        className
      )}
      onMouseDown={onMouseDown}
    >
      <div
        className={classList(
          'absolute right-0 w-0',
          'group-hover:w-0.5 group-hover:opacity-100',
          isDragging && ['w-0.5 opacity-100'],
          'transition-[opacity,width]',
          'will-change-[width,opacity]',
          'rounded-full bg-border-strongest',
          indicatorClassName
        )}
      />
    </div>
  );
};
